import posthog from "posthog-js";

const NEXT_OPAQUE_ERROR_MESSAGE =
  "An unexpected response was received from the server.";

/**
 * True when `err` is the opaque error Next.js surfaces for a failed server
 * action (a wrapped 500 / non-action response).
 */
export function isOpaqueServerActionError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return message === NEXT_OPAQUE_ERROR_MESSAGE;
}

/**
 * Extract Next.js server-action `error.digest` for PostHog correlation.
 * Returns `{ digest }` when present so it can be spread into capture extras;
 * server logs print the same digest, so a single string joins client error
 * tracking to backend logs.
 */
export function errorDigest(err: unknown): { digest?: string } {
  if (err && typeof err === "object" && "digest" in err) {
    const d = (err as { digest: unknown }).digest;
    if (typeof d === "string") return { digest: d };
  }
  return {};
}

/**
 * True when `err` is Next.js's `redirect()` control-flow error rather than a
 * real failure. Callers must rethrow it rather than handle it as a failure.
 */
export function isNextRedirectError(err: unknown): boolean {
  return errorDigest(err).digest?.startsWith("NEXT_REDIRECT") ?? false;
}

const PROBE_RESPONSE_HEADERS = [
  "content-type",
  "content-length",
  "server",
  "cf-ray",
  "cf-cache-status",
  "cf-mitigated",
  "x-vercel-id",
  "x-vercel-error",
  "x-vercel-cache",
  "x-matched-path",
  "x-robots-tag",
];

const PROBE_BODY_SNIPPET_LIMIT = 500;
const PROBE_TIMEOUT_MS = 5_000;

type NetworkInformation = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

export type ServerActionErrorContext = {
  /** Stable name identifying which action failed (e.g. "saveProfile"). */
  action: string;
  /** Extra properties merged into the PostHog event. */
  extras?: Record<string, unknown>;
};

export function captureServerActionError(
  err: unknown,
  context: ServerActionErrorContext,
): void {
  const isOpaqueNextError = isOpaqueServerActionError(err);
  const network = getNetworkInfo();

  posthog.captureException(err, {
    ...errorDigest(err),
    ...context.extras,
    action: context.action,
    opaque_next_error: isOpaqueNextError,
    network_online: typeof navigator !== "undefined" ? navigator.onLine : null,
    network_effective_type: network?.effectiveType ?? null,
    network_downlink: network?.downlink ?? null,
    network_rtt: network?.rtt ?? null,
  });

  if (!isOpaqueNextError || typeof window === "undefined") return;

  void runProbe(context, network);
}

async function runProbe(
  context: ServerActionErrorContext,
  network: NetworkInformation | null,
): Promise<void> {
  const path = window.location.pathname;
  const start = performance.now();

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    const response = await fetch(path, {
      method: "POST",
      headers: { "x-debug-probe": "1", "content-type": "text/plain" },
      body: "probe",
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    }).finally(() => clearTimeout(timeout));

    const text = await response.text().catch(() => "");
    const headers: Record<string, string> = {};
    for (const name of PROBE_RESPONSE_HEADERS) {
      const value = response.headers.get(name);
      if (value) headers[name] = value;
    }

    posthog.capture("server_action_error_probe", {
      ...context.extras,
      action: context.action,
      probe_path: path,
      probe_status: response.status,
      probe_status_text: response.statusText,
      probe_headers: headers,
      probe_body_snippet: text.slice(0, PROBE_BODY_SNIPPET_LIMIT),
      probe_body_length: text.length,
      probe_duration_ms: Math.round(performance.now() - start),
      network_effective_type: network?.effectiveType ?? null,
    });
  } catch (probeErr) {
    posthog.capture("server_action_error_probe", {
      ...context.extras,
      action: context.action,
      probe_path: path,
      probe_failed: true,
      probe_error_message:
        probeErr instanceof Error ? probeErr.message : String(probeErr),
      probe_error_name: probeErr instanceof Error ? probeErr.name : null,
      probe_duration_ms: Math.round(performance.now() - start),
      network_effective_type: network?.effectiveType ?? null,
    });
  }
}

function getNetworkInfo(): NetworkInformation | null {
  if (typeof navigator === "undefined") return null;
  const conn = (navigator as Navigator & { connection?: NetworkInformation })
    .connection;
  if (!conn) return null;
  return {
    effectiveType: conn.effectiveType,
    downlink: conn.downlink,
    rtt: conn.rtt,
    saveData: conn.saveData,
  };
}
